import { useSyncExternalStore } from 'react';
import type { components } from '@/api/generated-types';
import { isLocale, type Locale } from '@/i18n/translations';
import { messageSchema } from './response-schemas';
import {
  DonorError,
  locked,
  readStoredStrict,
  send,
  storeRequired,
  type ProtectedRequestOptions,
} from './http';

export type Profile = components['schemas']['MeProfileResponseDto'];
type Session = {
  phase: 'restoring' | 'guest' | 'signedIn' | 'unavailable';
  profile: Profile | null;
  epoch: number;
};
let state: Session = { phase: 'restoring', profile: null, epoch: 0 };
let access: { accessToken: string; accessTokenExpiresAt: string } | null = null;
let credentialGeneration = 0;
let accountLanguageRevision = 0;
const listeners = new Set<() => void>();
let boot: Promise<void> | undefined;
let refreshing: { fence: string; operation: Promise<void> } | undefined;
const channel = (() => {
  try {
    return new BroadcastChannel('giving:session');
  } catch {
    return null;
  }
})();
const SIGNED_OUT = 'giving.signed-out';
const ROTATION = 'giving.refresh-operation';
const FENCE = 'giving.session-fence';
const currentFence = (): string => {
  const value = readStoredStrict(FENCE, 'initial');
  if (typeof value !== 'string' || !value) throw new DonorError(0, 'STORAGE_REQUIRED');
  return value;
};
const isSignedOut = (): boolean => {
  const value = readStoredStrict(SIGNED_OUT, false);
  if (typeof value !== 'boolean') throw new DonorError(0, 'STORAGE_REQUIRED');
  return value;
};
const emit = (next: Session): void => {
  const changed = state.profile?.user.id !== next.profile?.user.id;
  state = { ...next, epoch: state.epoch + (changed ? 1 : 0) };
  if (changed) window.dispatchEvent(new Event('giving:identity-changed'));
  listeners.forEach((listener) => listener());
};
export const currentSession = (): Session => state;
export const useSession = (): Session =>
  useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => state,
  );

function preserveNewerAccountLanguage(profile: Profile, since: number): Profile {
  const current = state.profile;
  const locale = current?.preferences.locale;
  if (
    !current ||
    since === accountLanguageRevision ||
    current.user.id !== profile.user.id ||
    !isLocale(locale)
  )
    return profile;
  return {
    ...profile,
    user: { ...profile.user, locale },
    preferences: { ...profile.preferences, locale },
  };
}

async function accept(
  pair: { accessToken: string; accessTokenExpiresAt: string },
  locale: Locale,
  broadcast = true,
  expectedFence = currentFence(),
): Promise<void> {
  if (expectedFence !== currentFence() || isSignedOut())
    throw new DonorError(401, 'GIVING_ACTOR_CHANGED');
  access = pair;
  const generation = ++credentialGeneration;
  const languageRevision = accountLanguageRevision;
  let profile: Profile;
  try {
    profile = await send<Profile>('/me/profile', locale, { token: pair.accessToken });
  } catch (error) {
    if (generation !== credentialGeneration || expectedFence !== currentFence())
      throw new DonorError(401, 'GIVING_ACTOR_CHANGED');
    throw error;
  }
  if (
    generation !== credentialGeneration ||
    access !== pair ||
    expectedFence !== currentFence() ||
    isSignedOut()
  )
    throw new DonorError(401, 'GIVING_ACTOR_CHANGED');
  if (!profile.user?.id || !profile.user.emailVerifiedAt)
    throw new DonorError(401, 'SESSION_INVALID');
  emit({
    ...state,
    phase: 'signedIn',
    profile: preserveNewerAccountLanguage(profile, languageRevision),
  });
  localStorage.removeItem(SIGNED_OUT);
  if (broadcast)
    channel?.postMessage({ kind: 'tokens', pair, userId: profile.user.id, fence: expectedFence });
}

export async function restoreSession(locale: Locale, rejectedToken?: string): Promise<void> {
  const requestFence = currentFence();
  if (refreshing?.fence === requestFence) return refreshing.operation;
  const operation = locked('session', async () => {
    if (isSignedOut()) {
      access = null;
      emit({ ...state, phase: 'guest', profile: null });
      return;
    }
    if (requestFence !== currentFence()) throw new DonorError(401, 'GIVING_ACTOR_CHANGED');
    if (
      access &&
      Date.parse(access.accessTokenExpiresAt) > Date.now() + 5000 &&
      (!rejectedToken || access.accessToken !== rejectedToken)
    )
      return;
    const expectedFence = currentFence();
    const storedKey = readStoredStrict(ROTATION, null);
    if (storedKey !== null && (typeof storedKey !== 'string' || !storedKey))
      throw new DonorError(0, 'STORAGE_REQUIRED');
    const key = storedKey ?? crypto.randomUUID();
    storeRequired(ROTATION, key);
    try {
      const pair = await send<{ accessToken: string; accessTokenExpiresAt: string }>(
        '/auth/web/refresh',
        locale,
        { method: 'POST', body: { rotationKey: key } },
      );
      await accept(pair, locale, true, expectedFence);
      localStorage.removeItem(ROTATION);
    } catch (error) {
      if (expectedFence !== currentFence() || isSignedOut()) return;
      if (error instanceof DonorError && error.code === 'GIVING_ACTOR_CHANGED') return;
      access = null;
      if (error instanceof DonorError && error.status === 401) {
        localStorage.removeItem(ROTATION);
        emit({ ...state, phase: 'guest', profile: null });
      } else {
        emit({ ...state, phase: 'unavailable', profile: null });
        throw error;
      }
    }
  });
  refreshing = { fence: requestFence, operation };
  void operation
    .finally(() => {
      if (refreshing?.operation === operation) refreshing = undefined;
    })
    .catch(() => undefined);
  return operation;
}
export function bootstrapSession(locale: Locale): Promise<void> {
  boot ??= restoreSession(locale).catch(() => {
    emit({ ...state, phase: 'unavailable', profile: null });
  });
  return boot;
}

export async function accountRequest<T>(
  path: string,
  locale: Locale,
  options: ProtectedRequestOptions = {},
  expectedId = state.profile?.user.id,
): Promise<T> {
  if (!expectedId) throw new DonorError(401, 'SIGN_IN_REQUIRED');
  if (!access || new Date(access.accessTokenExpiresAt).getTime() < Date.now() + 5000)
    await restoreSession(locale);
  if (!access || state.profile?.user.id !== expectedId)
    throw new DonorError(401, 'GIVING_ACTOR_CHANGED');
  const epoch = state.epoch;
  const requestedToken = access.accessToken;
  try {
    const result = await send<T>(path, locale, { ...options, token: requestedToken });
    if (state.epoch !== epoch || state.profile?.user.id !== expectedId)
      throw new DonorError(401, 'GIVING_ACTOR_CHANGED');
    return result;
  } catch (error) {
    if (
      !(error instanceof DonorError) ||
      error.status !== 401 ||
      error.code === 'GIVING_ACTOR_CHANGED'
    )
      throw error;
    await restoreSession(locale, requestedToken);
    if (!access || state.profile?.user.id !== expectedId)
      throw new DonorError(401, 'GIVING_ACTOR_CHANGED');
    const retryEpoch = state.epoch;
    const result = await send<T>(path, locale, { ...options, token: access.accessToken });
    if (state.epoch !== retryEpoch || state.profile?.user.id !== expectedId)
      throw new DonorError(401, 'GIVING_ACTOR_CHANGED');
    return result;
  }
}

async function prepareSignIn(locale: Locale, replaceUnknownSession = false): Promise<void> {
  if (replaceUnknownSession || state.profile || isSignedOut()) {
    storeRequired(SIGNED_OUT, true);
    storeRequired(FENCE, crypto.randomUUID());
    credentialGeneration++;
    access = null;
    emit({ ...state, phase: 'guest', profile: null });
    channel?.postMessage({ kind: 'logout', fence: currentFence() });
    // Clear the old session before requesting a new proof cookie, never after it.
    await send('/auth/web/logout', locale, { method: 'POST' });
    localStorage.removeItem(SIGNED_OUT);
    localStorage.removeItem(ROTATION);
  }
}

export async function requestSignIn(email: string, locale: Locale, proof?: string): Promise<void> {
  await locked('session', async () => {
    await prepareSignIn(locale);
    await send<void>('/auth/web/magic-link/request', locale, {
      method: 'POST',
      body: { email, locale },
      ...(proof ? { proof } : {}),
    });
  });
}
export async function finishSignIn(
  locale: Locale,
  token?: string,
  code?: string,
): Promise<boolean> {
  if (code) return completeSignIn(locale, { kind: 'second-factor', code });
  if (!token) throw new DonorError(401, 'AUTH_LINK_INVALID');
  return completeSignIn(locale, { kind: 'link', token });
}

export function confirmEmailLink(
  locale: Locale,
  token: string,
  email: string,
  proof?: string,
): Promise<boolean> {
  return completeSignIn(locale, {
    kind: 'email-confirmation',
    token,
    email,
    ...(proof ? { proof } : {}),
  });
}

type SignInStep =
  | { kind: 'link'; token: string }
  | { kind: 'second-factor'; code: string }
  | { kind: 'email-confirmation'; token: string; email: string; proof?: string };

async function completeSignIn(locale: Locale, step: SignInStep): Promise<boolean> {
  return locked('session', async () => {
    // Another browser may still have a server session that bootstrap could not
    // read. Clear it first so a lost confirmation cannot restore the wrong account.
    if (step.kind === 'email-confirmation') await prepareSignIn(locale, true);
    if (isSignedOut()) throw new DonorError(401, 'SIGN_IN_REQUIRED');
    const expectedFence = currentFence();
    const result = await send<{
      requires2FA?: boolean;
      accessToken?: string;
      accessTokenExpiresAt?: string;
    }>(
      step.kind === 'second-factor' ? '/auth/web/2fa/verify' : '/auth/web/magic-link/consume',
      locale,
      {
        method: 'POST',
        body:
          step.kind === 'second-factor'
            ? { code: step.code }
            : {
                token: step.token,
                ...(step.kind === 'email-confirmation' ? { email: step.email } : {}),
              },
        ...(step.kind === 'email-confirmation' && step.proof ? { proof: step.proof } : {}),
      },
    );
    if (expectedFence !== currentFence() || isSignedOut())
      throw new DonorError(401, 'GIVING_ACTOR_CHANGED');
    if (result.requires2FA) return false;
    if (!result.accessToken || !result.accessTokenExpiresAt)
      throw new DonorError(401, 'SESSION_INVALID');
    storeRequired(FENCE, crypto.randomUUID());
    await accept(
      { accessToken: result.accessToken, accessTokenExpiresAt: result.accessTokenExpiresAt },
      locale,
    );
    localStorage.removeItem(ROTATION);
    return true;
  });
}
export async function logout(locale: Locale): Promise<void> {
  storeRequired(SIGNED_OUT, true);
  storeRequired(FENCE, crypto.randomUUID());
  credentialGeneration++;
  access = null;
  emit({ ...state, phase: 'guest', profile: null });
  channel?.postMessage({ kind: 'logout', fence: currentFence() });
  await locked('session', async () => {
    await send('/auth/web/logout', locale, { method: 'POST' });
    localStorage.removeItem(ROTATION);
  });
}
export async function reloadProfile(
  locale: Locale,
  expectedId = state.profile?.user.id,
): Promise<void> {
  const epoch = state.epoch;
  const languageRevision = accountLanguageRevision;
  const profile = await accountRequest<Profile>('/me/profile', locale, {}, expectedId);
  const current = state.profile;
  if (!current || state.epoch !== epoch || current.user.id !== profile.user.id) return;
  // Preserve a language saved after this read started without discarding other
  // fresh profile fields, such as a name saved concurrently.
  emit({ ...state, profile: preserveNewerAccountLanguage(profile, languageRevision) });
}

export async function setAccountLocale(
  locale: Locale,
  expectedId: string,
  expectedEpoch: number,
): Promise<void> {
  await locked('account-language', async () => {
    if (
      state.profile?.user.id !== expectedId ||
      state.epoch !== expectedEpoch ||
      state.phase !== 'signedIn'
    )
      throw new DonorError(401, 'GIVING_ACTOR_CHANGED');
    if (state.profile.preferences.locale === locale) return;
    const result = await accountRequest<
      Pick<components['schemas']['PreferencesResponseDto'], 'locale'>
    >('/me/preferences', locale, { method: 'PATCH', body: { locale } }, expectedId);
    if (result.locale !== locale) throw new DonorError(0, 'INVALID_API_RESPONSE');
    const profile = state.profile;
    if (!profile || profile.user.id !== expectedId || state.epoch !== expectedEpoch)
      throw new DonorError(401, 'GIVING_ACTOR_CHANGED');
    accountLanguageRevision++;
    emit({
      ...state,
      profile: {
        ...profile,
        user: { ...profile.user, locale },
        preferences: { ...profile.preferences, locale },
      },
    });
  });
}
channel?.addEventListener('message', (event: MessageEvent<unknown>) => {
  const parsed = messageSchema.safeParse(event.data);
  if (!parsed.success) return;
  const value = parsed.data;
  try {
    if (value.fence !== currentFence() || isSignedOut()) return;
  } catch {
    return;
  }
  if (value.kind === 'logout') {
    credentialGeneration++;
    access = null;
    emit({ ...state, phase: 'guest', profile: null });
  }
  if (value.kind === 'tokens' && value.pair && !isSignedOut()) {
    // Profile is fetched under the new token; old-account data is never broadcast.
    if (value.userId !== state.profile?.user.id)
      emit({ ...state, phase: 'restoring', profile: null });
    void accept(
      value.pair,
      document.documentElement.lang === 'en'
        ? 'en'
        : document.documentElement.lang === 'hu'
          ? 'hu'
          : 'ro',
      false,
      value.fence,
    ).catch((error: unknown) => {
      if (error instanceof DonorError && error.code === 'GIVING_ACTOR_CHANGED') return;
      access = null;
      emit({ ...state, phase: 'unavailable', profile: null });
    });
  }
});

window.addEventListener('storage', (event) => {
  if (event.key !== SIGNED_OUT || event.newValue !== 'true') return;
  credentialGeneration++;
  access = null;
  emit({ ...state, phase: 'guest', profile: null });
});
